import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip } from "recharts";
import { Gauge, MessageSquareReply, ThumbsUp, AlertTriangle, MapPin, Tags, ChevronRight } from "lucide-react";
import api from "../lib/api";
import { Label, InfoBadge } from "./common";
import { feedbackHref } from "../lib/feedbackLink";

const DRIVER_COLOR = { green: "#10B981", amber: "#F59E0B", red: "#EF4444", muted: "#CBD5E1" };

function DeltaChip({ delta, unit = "pts", positiveIsGood = true, testId }) {
  if (delta === null || delta === undefined) {
    return <span className="text-xs text-slate-400" data-testid={testId}>— vs last month</span>;
  }
  const up = delta > 0;
  const flat = delta === 0;
  const good = flat ? null : (up === positiveIsGood);
  const color = good === null ? "#64748B" : good ? "#10B981" : "#EF4444";
  const arrow = flat ? "→" : up ? "▲" : "▼";
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color }} data-testid={testId}>
      {arrow} {Math.abs(delta)} {unit} <span className="font-normal text-slate-400">vs last month</span>
    </span>
  );
}

function KpiTile({ icon: Icon, label, value, suffix, delta, unit, positiveIsGood = true, accent, help, testId }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 sv-rise" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1.5"><Label>{label}</Label>{help && <InfoBadge title={help.title} text={help.text} testId={`${testId}-info`} />}</span>
        <Icon className="h-4 w-4" style={{ color: accent || "#94A3B8" }} />
      </div>
      <div className="mt-3 flex items-baseline gap-1">
        <span className="sv-metric text-4xl font-bold leading-none" style={{ color: accent || "#0F172A" }}>
          {value === null || value === undefined ? "—" : value}
        </span>
        {suffix && value !== null && value !== undefined && (
          <span className="text-sm font-semibold text-slate-400">{suffix}</span>
        )}
      </div>
      <div className="mt-2">
        <DeltaChip delta={delta} unit={unit} positiveIsGood={positiveIsGood} testId={`${testId}-delta`} />
      </div>
    </div>
  );
}

function MiniTrend({ data }) {
  const clean = (data || []).map((d) => ({ ...d, value: d.value == null ? null : d.value }));
  const hasData = clean.some((d) => d.value != null);
  if (!hasData) {
    return <div className="flex h-[150px] items-center justify-center text-sm text-slate-400">Not enough history yet.</div>;
  }
  return (
    <div style={{ width: "100%", height: 150 }} data-testid="frontline-trend-chart">
      <ResponsiveContainer>
        <LineChart data={clean} margin={{ top: 10, right: 10, left: -24, bottom: 0 }}>
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
          <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#94A3B8" }} width={32} axisLine={false} tickLine={false} />
          <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }}
            formatter={(v) => [`${v}`, "Index"]} />
          <Line type="monotone" dataKey="value" stroke="#4338CA" strokeWidth={2.5}
            dot={{ r: 3, fill: "#4338CA" }} connectNulls />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function KpiRow({ k }) {
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <KpiTile icon={Gauge} label="Frontline Index" value={k.frontline_index.value} suffix={k.frontline_index.suffix}
        delta={k.frontline_index.delta} unit="pts" accent="#4338CA" testId="kpi-frontline-index"
        help={{ title: "Frontline Index", text: "Your overall service score out of 100, blending customer ratings with how confident we are in the sample. Higher is better — think of it as your frontline's report-card grade." }} />
      <KpiTile icon={MessageSquareReply} label="Response Rate" value={k.response_rate.value} suffix={k.response_rate.suffix}
        delta={k.response_rate.delta} unit="pts" testId="kpi-response-rate"
        help={{ title: "Response Rate", text: "Of the customers who scanned a QR/NFC code, the share who actually left feedback. A higher rate means more people are telling you about their experience." }} />
      <KpiTile icon={ThumbsUp} label="Favourable" value={k.favourable.value} suffix={k.favourable.suffix}
        delta={k.favourable.delta} unit="pts" accent="#10B981" testId="kpi-favourable"
        help={{ title: "Favourable", text: "The percentage of ratings that were positive (4 or 5 stars). It's the quickest read on how many customers walked away happy." }} />
      <KpiTile icon={AlertTriangle} label={`At-risk ${k.at_risk.label}`} value={k.at_risk.value}
        delta={k.at_risk.delta} unit={k.at_risk.unit} positiveIsGood={false} accent="#EF4444" testId="kpi-at-risk"
        help={{ title: "At-risk", text: "How many branches (or employees, for a store) are scoring below the healthy mark and need attention. Lower is better — ideally zero." }} />
    </div>
  );
}

function DriverBar({ dr, onClick }) {
  const color = DRIVER_COLOR[dr.status];
  const sample = dr.sample != null ? ` · ${dr.sample} rating${dr.sample === 1 ? "" : "s"}` : "";
  const disabled = !onClick || !dr.sample;
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={disabled ? `${dr.label}${sample} — no ratings yet` : `See ratings behind ${dr.label}${sample}`}
      data-testid={`driver-${dr.key}`}
      className={`w-full text-left rounded-lg px-2 py-1 -mx-2 transition-colors ${disabled ? "cursor-default" : "cursor-pointer hover:bg-slate-50"}`}
    >
      <div className="mb-1 flex items-center justify-between text-sm">
        <span className="flex items-center gap-2 text-slate-600"><span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} />{dr.label}</span>
        <span className="sv-metric font-bold" style={{ color: dr.score == null ? "#94A3B8" : color }}>{dr.score == null ? "—" : dr.score}</span>
      </div>
      <div className="h-2 w-full rounded-full bg-slate-100"><div className="h-2 rounded-full transition-all" style={{ width: `${dr.score || 0}%`, background: color }} /></div>
    </button>
  );
}

function RankedRow({ i, label, onClick, testId, children }) {
  return (
    <button onClick={onClick} className="group flex w-full items-center justify-between rounded-lg px-2 py-2 text-left transition-colors hover:bg-slate-50" data-testid={testId}>
      <span className="flex items-center gap-2 text-sm font-semibold text-slate-700"><span className="text-slate-400">{i + 1}.</span>{label}</span>
      <span className="flex items-center gap-1.5">{children}
        <ChevronRight className="h-4 w-4 text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-slate-500" /></span>
    </button>
  );
}

function RiskLocations({ rows, navigate }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="frontline-risk-locations">
      <span className="flex items-center gap-1.5"><Label className="flex items-center gap-2"><MapPin className="h-4 w-4 text-rose-500" /> Highest-risk locations</Label>
        <InfoBadge title="Highest-risk locations" testId="risk-loc-info"
          text="The branches with the lowest service scores this month, ranked worst-first. These are where management attention will have the biggest impact. Click one to open its dashboard." /></span>
      <div className="mt-3 space-y-2">
        {rows.length === 0 ? <p className="py-6 text-center text-sm text-slate-400">No location data this month yet.</p>
          : rows.map((l, i) => (
            <RankedRow key={l.name} i={i} label={l.name} onClick={() => navigate(`/app/store?location=${l.id}`)} testId={`risk-loc-${i}`}>
              <span className="sv-metric font-bold text-slate-800">{l.index} <span className="text-xs font-normal text-slate-400">/ 100</span></span>
            </RankedRow>))}
      </div>
    </div>
  );
}

function Themes({ rows, navigate, wide }) {
  return (
    <div className={`rounded-xl border border-slate-200 bg-white p-5 ${wide ? "md:col-span-2" : ""}`} data-testid="frontline-themes">
      <span className="flex items-center gap-1.5"><Label className="flex items-center gap-2"><Tags className="h-4 w-4 text-indigo-500" /> Top operational themes</Label>
        <InfoBadge title="Top operational themes" testId="themes-info"
          text="The most common problems customers mentioned in negative feedback this month. Tackling the top theme usually lifts scores fastest. Click one to read the feedback behind it." /></span>
      <div className="mt-3 space-y-2">
        {rows.length === 0 ? <p className="py-6 text-center text-sm text-slate-400">No negative themes flagged this month. 🎉</p>
          : rows.map((t, i) => (
            <RankedRow key={t.label} i={i} label={t.label} onClick={() => navigate(`/app/feedback?category=${encodeURIComponent(t.label)}`)} testId={`theme-${i}`}>
              <span className="rounded-full bg-rose-50 px-2 py-0.5 text-xs font-bold text-rose-600">{t.count}</span>
            </RankedRow>))}
      </div>
    </div>
  );
}

export default function FrontlineExperience({ locationId }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    setD(null);
    setErr(null);
    const q = locationId ? `?location_id=${locationId}` : "";
    api.get(`/reports/frontline${q}`).then((r) => setD(r.data)).catch((e) => { console.warn("Frontline load failed", e); setErr("Could not load the frontline overview."); });
  }, [locationId]);

  if (err) return null;
  if (!d) return <div className="text-sm text-slate-400" data-testid="frontline-loading">Loading frontline overview…</div>;

  const k = d.kpis;
  const isStore = d.scope === "store";

  return (
    <div className="space-y-6" data-testid="frontline-experience">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Label>Frontline Experience</Label>
          <InfoBadge title="Frontline Experience" testId="frontline-info"
            text="A monthly snapshot of how your frontline is performing in customers' eyes — the headline score, how many customers responded, how positive they were, and where the risks are." />
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">
            {d.period_label} · {isStore ? (d.location?.name || "This location") : "All locations"}
          </span>
        </div>
      </div>

      <KpiRow k={k} />

      {/* Drivers + trend */}
      <div className="grid gap-6 md:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-5 md:col-span-2" data-testid="frontline-drivers">
          <span className="flex items-center gap-1.5"><Label>Service Drivers · this month</Label>
            <InfoBadge title="Service Drivers" testId="drivers-info"
              text="How your frontline is scoring on each rating dimension customers tap on the feedback form (e.g. Friendliness, Speed, Accuracy). Each is averaged 0–100 for the month: green ≥ 75, amber 60–74, red below 60. Change these dimensions in Settings → Touchpoints." /></span>
          <div className="mt-4 space-y-4">{d.drivers.map((dr) => (
            <DriverBar key={dr.key} dr={dr}
              onClick={() => navigate(feedbackHref({ dimension: dr.key, location_id: locationId, days: 30 }))} />
          ))}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="frontline-trend">
          <span className="flex items-center gap-1.5"><Label>6-Month Trend</Label>
            <InfoBadge title="6-Month Trend" testId="trend-info"
              text="How your Frontline Index has moved over the last six months. A rising line means service is improving; a dip is an early warning to dig in." /></span>
          <div className="mt-3"><MiniTrend data={d.trend} /></div>
        </div>
      </div>

      {/* Risk locations + themes */}
      <div className="grid gap-6 md:grid-cols-2">
        {!isStore && <RiskLocations rows={d.risk_locations} navigate={navigate} />}
        <Themes rows={d.themes} navigate={navigate} wide={isStore} />
      </div>
    </div>
  );
}
